import React, { useEffect } from 'react';
import { KeyRound } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useApiKeyManager } from '../ApiKey';
import { saveIntendedUrl } from '../../utils/persistentNavigation';
import { trackEvent } from '../../utils/analytics';

/**
 * SkillsApiKeyCTA — the API-key entry point on the /skills (Agent-to-agent Skills) page.
 *
 * Design: a QUIET inline text link, not a boxed button — the hero is minimal (title, subtitle,
 * "Works with" strip) and a large antd button broke its elegance. The link sits in the same
 * typographic register as the "Works with" label; hover brightens it.
 *
 * /skills is a PUBLIC MarketingRoute (also reactSnap-prerendered), so behavior is deterministic
 * without auth:
 *   - Logged in  → opens the ApiKeyManager modal (label adapts: 0 keys "Get your API key",
 *                  N keys "Manage API keys"). Provider wraps the Router (App.js) — available here.
 *   - Logged out → "Sign up to get an API key" → signup with `/skills?intent=get_api_key`
 *                  stashed via persistentNavigation (the real post-auth routing rail; the old
 *                  `?return=` query param was dead code — /signup never read it). On return,
 *                  the intent effect below auto-opens the key modal.
 */
const SkillsApiKeyCTA = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const { activeCount, loading, hasLoadedOnce, open } = useApiKeyManager();

  // Post-signup auto-open: consume ?intent=get_api_key after the visitor lands
  // back on /skills from the signup flow.
  useEffect(() => {
    if (!currentUser) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('intent') === 'get_api_key') {
      params.delete('intent');
      const url = new URL(window.location);
      url.search = params.toString();
      window.history.replaceState({}, document.title, url.toString());
      open();
    }
  }, [currentUser, open]);

  let label;
  let onClick;
  if (!currentUser) {
    label = 'Sign up to get an API key';
    onClick = () => {
      trackEvent('cta_click', { cta_name: 'get_api_key', location: 'skills_hero', page: 'skills' });
      saveIntendedUrl('/skills?intent=get_api_key');
      navigate('/signup');
    };
  } else if (loading && !hasLoadedOnce) {
    label = 'API keys';
    onClick = open;
  } else {
    label = activeCount === 0 ? 'Get your API key' : 'Manage API keys';
    onClick = () => {
      trackEvent('cta_click', { cta_name: 'get_api_key', location: 'skills_hero', page: 'skills' });
      open();
    };
  }

  return (
    <button type="button" className="skills-key-link" onClick={onClick}>
      <KeyRound size={13} />
      <span>{label}</span>
      <span className="skills-key-link-arrow" aria-hidden="true">→</span>
    </button>
  );
};

export default SkillsApiKeyCTA;
